/**
 * Shared renderers + label maps for the warehouse Processes screens. Plain,
 * sales/CS-friendly labels; status → StatusPill tone; type → tinted Chip; a
 * source → destination route string from the process endpoints.
 */
import type { ReactNode } from 'react';
import { ArrowRight } from 'lucide-react';
import { Chip, type ChipTone } from '@vnetwork/ui/primitives';
import { StatusPill } from '@vnetwork/ui/data';
import type {
  ApproverRole,
  ProcessEndpoint,
  ProcessStatus,
  ProcessType,
} from '@/api/capacities';

/** Human label for each process type (e.g. import_purchase → "Import (Purchase)"). */
export const PROCESS_TYPE_LABEL: Record<ProcessType, string> = {
  import_purchase: 'Import (Purchase)',
  import_return: 'Import (Return)',
  export_sale: 'Export (Sale)',
  export_loan: 'Export (Loan)',
  export_delivery: 'Export (Delivery)',
  internal_transfer: 'Internal Transfer',
  inventory_check: 'Inventory Check',
};

/** Short descriptive copy per type — used on the create-wizard type cards. */
export const PROCESS_TYPE_DESCRIPTION: Record<ProcessType, string> = {
  import_purchase: 'Receive newly purchased goods from a supplier.',
  import_return: 'Take back items previously loaned or delivered out.',
  export_sale: 'Ship sold items to a customer.',
  export_loan: 'Loan items out to a customer or partner.',
  export_delivery: 'Deliver items to a datacenter or location.',
  internal_transfer: 'Move items between two warehouse locations.',
  inventory_check: 'Audit on-hand stock at a location.',
};

export const PROCESS_STATUS_LABEL: Record<ProcessStatus, string> = {
  draft: 'Draft',
  pending_approval: 'Pending Approval',
  approved: 'Approved',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

const PROCESS_STATUS_TONE: Record<
  ProcessStatus,
  'neutral' | 'warning' | 'info' | 'success' | 'danger'
> = {
  draft: 'neutral',
  pending_approval: 'warning',
  approved: 'info',
  completed: 'success',
  cancelled: 'danger',
};

export const APPROVER_ROLE_LABEL: Record<ApproverRole, string> = {
  tech_lead: 'Tech Lead',
  storekeeper: 'Storekeeper',
};

export const processTypeLabel = (type?: ProcessType): string =>
  type ? PROCESS_TYPE_LABEL[type] ?? type : '';

export const processStatusLabel = (status?: ProcessStatus): string =>
  status ? PROCESS_STATUS_LABEL[status] ?? status : '';

const muted = { color: 'var(--vn-fg-muted)' } as const;

/**
 * Type → Chip tone, mirroring the legacy warehouse palette: imports = primary,
 * export (sale) = danger, export (loan/delivery) = warning, internal transfer =
 * info, inventory check = neutral.
 */
const PROCESS_TYPE_TONE: Record<ProcessType, ChipTone> = {
  import_purchase: 'primary',
  import_return: 'primary',
  export_sale: 'danger',
  export_loan: 'warning',
  export_delivery: 'warning',
  internal_transfer: 'info',
  inventory_check: 'neutral',
};

/** Process type → plain-label tinted Chip. */
export function processTypeCell(type?: ProcessType): ReactNode {
  if (!type) return <span style={muted}>—</span>;
  return <Chip tone={PROCESS_TYPE_TONE[type] ?? 'neutral'}>{PROCESS_TYPE_LABEL[type] ?? type}</Chip>;
}

/** Process status → StatusPill (draft=neutral, pending=warning, approved=info…). */
export function processStatusCell(status?: ProcessStatus): ReactNode {
  if (!status) return <span style={muted}>—</span>;
  return (
    <StatusPill tone={PROCESS_STATUS_TONE[status] ?? 'neutral'}>
      {PROCESS_STATUS_LABEL[status] ?? status}
    </StatusPill>
  );
}

export const endpointName = (e?: ProcessEndpoint): string => e?.name?.trim() || '';

/** `source.name → destination.name` route cell; falls back to em dash. */
export function processRouteCell(
  source?: ProcessEndpoint,
  destination?: ProcessEndpoint,
): ReactNode {
  const from = endpointName(source);
  const to = endpointName(destination);
  if (!from && !to) return <span style={muted}>—</span>;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-1)', overflowWrap: 'anywhere' }}>
      <span>{from || '—'}</span>
      <ArrowRight size={14} style={{ color: 'var(--vn-fg-muted)', flexShrink: 0 }} />
      <span>{to || '—'}</span>
    </span>
  );
}
